import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect } from "react";
import { useStringMap } from "../utilities/string-map/useStringMap";
import { CloseIcon } from "../icons/CloseIcon";
import { ConfirmIcon } from "../icons/ConfirmIcon";
import { ErrorIcon } from "../icons/ErrorIcon";
import { InformationIcon } from "../icons/InformationIcon";
import { WarningIcon } from "../icons/WarningIcon";
import { CircularProgress } from "../progress/circular-progress/CircularProgress";
import * as Styled from "./ToastMessage.styled";
import { stringMapDefault } from "./ToastMessage.stringMap";
export const TOASTMESSAGE_LEVELS = {
    confirm: {
        palette: "expressivePositive",
        icon: ConfirmIcon
    },
    error: {
        palette: "expressiveNegative",
        icon: ErrorIcon
    },
    informative: {
        palette: "expressiveInformative",
        icon: InformationIcon
    },
    warning: {
        palette: "expressiveWarning",
        icon: WarningIcon
    },
    progress: {
        palette: "expressiveInformative",
        icon: CircularProgress
    }
};
const componentName = "ToastMessage";
export const ToastMessage = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { autoDismissDelay, children, content, icon, isDismissible = true, level = "warning", onDismiss, shouldAutoDismiss, stringMap: stringMapProp, tabIndex = -1, title, toastType = "default", toastMessageRef: messageRefProp, id, ...otherProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    useEffect(() => {
        if (shouldAutoDismiss === true && onDismiss) {
            const autoDismissTimer = setTimeout(() => onDismiss && onDismiss(id), autoDismissDelay);
            return () => clearTimeout(autoDismissTimer);
        }
        return undefined;
    }, [autoDismissDelay, id, onDismiss, shouldAutoDismiss]);
    const closeOnEsc = (event) => {
        if (event.key === "Escape" && onDismiss) {
            onDismiss(id);
        }
    };
    const messageLevel = TOASTMESSAGE_LEVELS[level] === undefined ? "warning" : level;
    const currentPalette = TOASTMESSAGE_LEVELS[messageLevel].palette;
    const Icon = TOASTMESSAGE_LEVELS[messageLevel].icon;
    return (_jsx(Styled.ToastMessage, { "data-testid": "ToastMessage", onKeyDown: isDismissible ? closeOnEsc : undefined, palette: currentPalette, ref: ref, id: id ? String(id) : undefined, toastType: toastType, ...otherProps, children: _jsxs(Styled.MessageContentWrapper, { toastType: toastType, children: [_jsx(Styled.IconWrapper, { toastType: toastType, palette: currentPalette, children: icon ? (icon) : messageLevel === "progress" ? (_jsx(Styled.CustomIcon, { palette: TOASTMESSAGE_LEVELS[level].palette, children: _jsx(CircularProgress, { size: 22, "aria-hidden": "true", color: "currentColor" }) })) : (_jsx(Icon, { palette: currentPalette, "aria-hidden": "true" })) }), _jsx(Styled.Content, { "data-testid": "ToastMessageContent", ref: messageRefProp, role: "alert", "aria-live": "off", 
                    // TODO: DES-3469 - restore role="alert"
                    tabIndex: tabIndex, children: _jsxs(_Fragment, { children: [title && _jsx(Styled.Title, { children: title }), content || children] }) }), isDismissible && (_jsx(Styled.DismissButton, { onClick: onDismiss ? () => onDismiss(id) : undefined, toastType: toastType, children: _jsx(CloseIcon, { palette: currentPalette, color: "base", size: 16, role: "img", "aria-label": stringMap.ariaLabelCloseToast }) }))] }) }));
});
ToastMessage.defaultProps = {
    autoDismissDelay: 3000,
    isDismissible: true,
    level: "warning",
    stringMap: {
        ariaLabelCloseToast: "Close"
    },
    toastType: "default"
};
